import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { setColor } from "../../../../assets/styles/styles";
import Title from "../../components/Title";
import PinkIntraverseWithoutBorderLogo from "../../../../assets/images/logos/intraPinkWithoutBorder.png";
import Pagination from "../../../../components/common/pagination/Pagination";
import { getUsersRooms } from "../../../../api/RoomsController";
import { getFileUrl } from "../../../../api/FilesController";
import LoadingIcon from "../../../../components/common/loading/LoadingIcon";

const MyRooms = () => {
    const user = useSelector((state) => state.user);
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();
    const ROWS_PER_PAGE = 9;
    const [visibleRows, setVisibleRows] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [roomData, setRoomData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const allRooms = await getUsersRooms(user.userId);

            const roomsWithUrls = await Promise.all(
                allRooms.map(async (room) => {
                    if (room.roomDetailsImageId) {
                        const getFile = await getFileUrl(room.roomDetailsImageId);
                        if (getFile?.status === 200) {
                            room.roomDetailsImageUrl = getFile.data;
                        }
                    }
                    return room;
                })
            );

            setRoomData(roomsWithUrls);

            const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
            const endIndex = startIndex + ROWS_PER_PAGE;
            setVisibleRows(roomsWithUrls.slice(startIndex, endIndex));
            setTotalPages(Math.ceil(roomsWithUrls.length / ROWS_PER_PAGE));
            setLoading(false);
        };

        fetchData();
    }, [currentPage]);

    return (
        <>
            {loading && (
                <LoadingIconContainer>
                    {" "}
                    <LoadingIcon />
                </LoadingIconContainer>
            )}
            {!loading && (
                <PageContainer>
                    <ScrollArea>
                        <CardTable>
                            <TableRow>
                                <Title
                                    title={"My Rooms"}
                                    description={
                                        "Manage your rooms here. Click on a room to personalize or customize it."
                                    }
                                />
                            </TableRow>
                        </CardTable>

                        <CardsContainer>
                            {visibleRows.map((item, index) => (
                                <Card
                                    key={index}
                                    onClick={() => navigate(`/console/${user.userId}/my-rooms/${item.roomId}`)}
                                >
                                    {item.roomDetailsImageUrl ? (
                                        <CardImage src={item.roomDetailsImageUrl} alt={item.roomName} />
                                    ) : (
                                        <DefaultLogo src={PinkIntraverseWithoutBorderLogo} alt="Default Room" />
                                    )}

                                    <CardLabel>{item.roomName ? item.roomName : "Untitled Room"}</CardLabel>

                                    <InfoTable>
                                        <InfoRow>
                                            <span>Created:</span>
                                            <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                                        </InfoRow>
                                        <InfoRow>
                                            <span>Visitors:</span>
                                            <span>{item.totalRoomUsers || 0}</span>
                                        </InfoRow>
                                        <InfoRow>
                                            <span>Type:</span>
                                            <span>{item.roomType || "Unknown"}</span>
                                        </InfoRow>
                                        <InfoRow>
                                            <span>Room ID:</span>
                                            <span>{item.roomId || "Unknown"}</span>
                                        </InfoRow>
                                    </InfoTable>
                                </Card>
                            ))}
                        </CardsContainer>
                    </ScrollArea>
                    <FixedPagination>
                        <Pagination
                            totalNumber={roomData?.length}
                            visibleRows={visibleRows}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            totalPages={totalPages}
                            tag={"Rooms"}
                        />
                    </FixedPagination>
                </PageContainer>
            )}
        </>
    );
};

export default MyRooms;

const CardTable = styled.div`
    display: flex;
    flex-direction: column;
`;

const TableRow = styled.div`
    display: flex;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const TableColumn = styled.div`
    flex: 1;
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 25px;
    margin: 20px 0;
    min-height: 100vh;
`;
const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;

const LogoWrapper = styled.div`
    width: 48px;
    height: 48px;
    background-color: #333; /* optional */
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const MobileLogo = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

const TitleCard = styled.h2`
    font-size: 20px;
    font-weight: 600;
`;

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 4px 0;
    gap: 8px;

    &:last-child {
        border-bottom: none;
    }

    /* Label side */
    span:first-child {
        font-weight: 600;
        color: #f5c5ff; /* subtle pink accent */
        white-space: nowrap;
    }

    /* Value side */
    span:last-child {
        text-align: right;
        flex: 1;
        overflow-wrap: anywhere; /* handles long UUID */
    }
`;
const InfoTable = styled.div`
    display: flex;
    flex-direction: column;
    width: 90%;
    margin-left: 10px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.8);
    word-break: break-word;
`;

const CardsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: 12px;
    width: 100%;
    margin-top: 20px;
    box-sizing: border-box;
`;

const Card = styled.div`
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
    border-radius: 10px;
    box-sizing: border-box;
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(6px);
    cursor: pointer;
    transition:
        transform 200ms ease,
        box-shadow 200ms ease;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-height: 100px;
    padding: 10px 10px;
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 6px 20px rgba(32, 12, 50, 0.4);
    }
`;

const CardImage = styled.img`
    width: 100%;
    height: 80px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
`;

const CardLabel = styled.div`
    font-size: 13px;
    color: #fff;
    text-align: center;
    font-weight: 700;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding-bottom: 15px;
`;
const DefaultLogo = styled.img`
    width: 55px;
    height: 55px;
    object-fit: contain;
    margin: 20px auto 0;
    opacity: 0.8;
`;

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    position: relative;
    overflow: hidden;
`;

const ScrollArea = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 20px 20px 120px; /* space for fixed pagination */
    box-sizing: border-box;
`;

const FixedPagination = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 0px 9rem 10px 8rem;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const LoadingIconContainer = styled.div`
    margin-top: 200px;
    display: flex;
    justify-content: center;
    width: 100%;
`;
